import { Component } from '@theme/component';

class ProductGallery extends Component {
  static refs = {
    mainImage: 'data-ref="galleryMainImage"',
    prevButton: 'data-ref="galleryPrev"',
    nextButton: 'data-ref="galleryNext"',
    counter: 'data-ref="galleryCounter"',
    currentIndex: 'data-ref="galleryCurrentIndex"',
    totalCount: 'data-ref="galleryTotalCount"',
    thumbnails: 'data-ref="galleryThumbnail"',
    showAll: 'data-ref="galleryShowAll"'
  };

  #currentIndex = 0;
  #isTransitioning = false;

  /** @type {Array<{id: string, url: string, alt: string}>} */
  #media = [];

  #onPrev = null;
  #onNext = null;
  #onShowAll = null;

  /** @type {Array<{thumb: Element, handler: Function}>} */
  #thumbHandlers = null;

  /** @type {Array<{el: Element, type: string, handler: Function}>} */
  #swipeHandlers = null;

  connectedCallback() {
    super.connectedCallback();
    this.#parseMedia();
    if (this.#media.length > 1) {
      this.#bindEvents();
      this.#bindSwipe();
      this.#preloadAdjacent(0);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const prev = this.refs.galleryPrev;
    const next = this.refs.galleryNext;
    const showAll = this.refs.galleryShowAll;

    if (prev && this.#onPrev) {
      prev.removeEventListener('click', this.#onPrev);
      this.#onPrev = null;
    }

    if (next && this.#onNext) {
      next.removeEventListener('click', this.#onNext);
      this.#onNext = null;
    }

    if (this.#thumbHandlers) {
      this.#thumbHandlers.forEach(({ thumb, handler }) => {
        thumb.removeEventListener('click', handler);
      });
      this.#thumbHandlers = null;
    }

    if (showAll && this.#onShowAll) {
      showAll.removeEventListener('click', this.#onShowAll);
      this.#onShowAll = null;
    }

    if (this.#swipeHandlers) {
      this.#swipeHandlers.forEach(({ el, type, handler }) => {
        el.removeEventListener(type, handler);
      });
      this.#swipeHandlers = null;
    }
  }

  #parseMedia() {
    const dataAttr = this.getAttribute('data-media');
    if (!dataAttr) return;
    try {
      this.#media = JSON.parse(dataAttr);
    } catch (e) {
      // no media data
    }
  }

  #bindEvents() {
    const prev = this.refs.galleryPrev;
    const next = this.refs.galleryNext;

    if (prev) {
      this.#onPrev = (e) => {
        e.preventDefault();
        this.#navigate(-1);
      };
      prev.addEventListener('click', this.#onPrev);
    }

    if (next) {
      this.#onNext = (e) => {
        e.preventDefault();
        this.#navigate(1);
      };
      next.addEventListener('click', this.#onNext);
    }

    const thumbnails = this.querySelectorAll('[data-ref="galleryThumbnail"]');
    this.#thumbHandlers = [];
    thumbnails.forEach((thumb, i) => {
      const handler = (e) => {
        e.preventDefault();
        this.#showMedia(i);
      };
      thumb.addEventListener('click', handler);
      this.#thumbHandlers.push({ thumb, handler });
    });

    const showAll = this.refs.galleryShowAll;
    if (showAll) {
      this.#onShowAll = () => {
        this.#expandThumbnails();
      };
      showAll.addEventListener('click', this.#onShowAll);
    }
  }

  #bindSwipe() {
    const viewport = this.querySelector('.product-gallery__viewport');
    if (!viewport) return;

    const THRESHOLD = 40;
    // Require horizontal motion to clearly dominate vertical motion so that
    // diagonal drags still scroll the page instead of flipping the gallery.
    const V_RATIO = 1.2;

    let startX = 0;
    let startY = 0;
    let tracking = false;
    let swipeAxis = null; // 'horizontal' | 'vertical' once established

    const onTouchStart = (e) => {
      if (this.#isTransitioning) return;
      const t = e.changedTouches[0];
      startX = t.clientX;
      startY = t.clientY;
      tracking = true;
      swipeAxis = null;
    };

    const onTouchMove = (e) => {
      if (!tracking) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;

      if (!swipeAxis) {
        if (Math.abs(dx) > THRESHOLD && Math.abs(dx) > Math.abs(dy) * V_RATIO) {
          // Horizontal swipe: lock the axis, prevent browser scroll jitter,
          // and navigate once per gesture.
          swipeAxis = 'horizontal';
          if (e.cancelable) e.preventDefault();
          this.#navigate(dx < 0 ? 1 : -1);
        } else if (Math.abs(dy) > THRESHOLD) {
          // Vertical drag: yield to native page scroll (no preventDefault).
          swipeAxis = 'vertical';
        }
      }
    };

    const onTouchEnd = () => {
      tracking = false;
      swipeAxis = null;
    };

    // touchstart/touchend stay passive; touchmove needs { passive: false }
    // only to call preventDefault for an established horizontal swipe — a
    // vertical drag is never prevented, so page scrolling is not blocked.
    viewport.addEventListener('touchstart', onTouchStart, { passive: true });
    viewport.addEventListener('touchmove', onTouchMove, { passive: false });
    viewport.addEventListener('touchend', onTouchEnd, { passive: true });

    this.#swipeHandlers = [
      { el: viewport, type: 'touchstart', handler: onTouchStart },
      { el: viewport, type: 'touchmove', handler: onTouchMove },
      { el: viewport, type: 'touchend', handler: onTouchEnd }
    ];
  }

  #preloadAdjacent(index) {
    const len = this.#media.length;
    if (len === 0) return;
    const prevIdx = (index - 1 + len) % len;
    const nextIdx = (index + 1) % len;
    [prevIdx, nextIdx].forEach(i => {
      if (i !== index) {
        const img = new Image();
        img.src = this.#media[i].url;
      }
    });
  }

  #navigate(direction) {
    if (this.#isTransitioning) return;
    const len = this.#media.length;
    if (len === 0) return;
    const newIndex = (this.#currentIndex + direction + len) % len;
    this.#switchTo(newIndex);
  }

  #showMedia(index) {
    if (this.#isTransitioning) return;
    if (index === this.#currentIndex) return;
    this.#switchTo(index);
  }

  #switchTo(index) {
    const mainImage = this.refs.galleryMainImage;
    if (!mainImage) return;

    const target = this.#media[index];
    if (!target) return;

    this.#isTransitioning = true;

    const self = this;
    const FADE = 150;
    let settled = false;

    const applySource = () => {
      // Remove the responsive srcset/sizes attributes so the browser stops
      // re-selecting the original image from srcset and honors the new src.
      mainImage.removeAttribute('srcset');
      mainImage.removeAttribute('sizes');
      mainImage.src = target.url;
      mainImage.alt = target.alt || '';
    };

    const release = () => {
      mainImage.style.opacity = '';
      self.#isTransitioning = false;
    };

    // Fade the current image out, swap sources, then fade back in.
    const swap = () => {
      if (settled) return;
      settled = true;
      clearTimeout(fallbackTimer);

      mainImage.style.opacity = '0';
      setTimeout(() => {
        applySource();
        mainImage.style.opacity = '1';
        setTimeout(release, FADE);
      }, FADE);
    };

    // Wait for the target image to be ready so swapping never flashes an
    // empty <img>.
    const preloader = new Image();
    preloader.onload = swap;
    preloader.onerror = swap;
    preloader.src = target.url;

    // Hard fallback: never leave the gallery locked if the preloader never
    // settles, and ignore a preloader that resolves afterwards.
    const fallbackTimer = setTimeout(swap, FADE + 1000);

    this.#currentIndex = index;
    this.#preloadAdjacent(index);
    this.#updateUI();
  }

  #updateUI() {
    const currentEl = this.refs.galleryCurrentIndex;
    if (currentEl) {
      currentEl.textContent = String(this.#currentIndex + 1);
    }

    const thumbnails = this.querySelectorAll('[data-ref="galleryThumbnail"]');
    thumbnails.forEach((thumb, i) => {
      thumb.classList.toggle('active', i === this.#currentIndex);
    });

  }

  #expandThumbnails() {
    const container = this.querySelector('[data-ref="galleryThumbnailContainer"]');
    if (!container) return;

    const hiddenThumbs = container.querySelectorAll('[data-ref="galleryThumbnail"][hidden]');
    hiddenThumbs.forEach(t => t.removeAttribute('hidden'));
    /* Force-load thumbs that were sitting in a display:none ([hidden]) ancestor:
       their lazy-load viewport intersection never fired while hidden, so without
       this they would stay in a perpetual loading state when revealed. */
    hiddenThumbs.forEach(t => {
      const img = t.querySelector('img');
      if (!img || img.complete) return;
      img.loading = 'eager';
      const src = img.currentSrc || img.src;
      if (src) img.src = src;
    });

    const showAll = this.refs.galleryShowAll;
    if (showAll) {
      showAll.setAttribute('hidden', '');
    }
  }
}

if (!customElements.get('product-gallery')) {
  customElements.define('product-gallery', ProductGallery);
}
